import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Printer, Download, Loader2, Info } from 'lucide-react';
import { type PrintSettings, DEFAULT_PRINT_SETTINGS } from '@/utils/threeMfExporter';

interface PrintSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultModelName?: string;
  /** Called when the user confirms — caller handles the actual export */
  onConfirm: (settings: PrintSettings) => void | Promise<void>;
}

export function PrintSettingsModal({
  open,
  onOpenChange,
  defaultModelName = 'gienox-model',
  onConfirm,
}: PrintSettingsModalProps) {
  const [settings, setSettings] = useState<PrintSettings>({
    ...DEFAULT_PRINT_SETTINGS,
    modelName: defaultModelName,
  });
  const [isExporting, setIsExporting] = useState(false);

  const handleConfirm = async () => {
    setIsExporting(true);
    try {
      await onConfirm(settings);
      onOpenChange(false);
    } finally {
      setIsExporting(false);
    }
  };

  const set = <K extends keyof PrintSettings>(k: K, v: PrintSettings[K]) =>
    setSettings((prev) => ({ ...prev, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full max-w-md border-adam-neutral-700 bg-adam-bg-secondary-dark text-adam-text-primary"
        aria-describedby="print-settings-description"
      >
        {/* Header */}
        <DialogHeader className="gap-1">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-adam-blue/20">
              <Printer className="h-4 w-4 text-adam-blue" />
            </div>
            <DialogTitle className="text-base font-semibold text-adam-text-primary">
              Print Settings
            </DialogTitle>
          </div>
          <DialogDescription
            id="print-settings-description"
            className="text-xs text-adam-neutral-400"
          >
            Configure your model for 3D printing. The .3MF file works with
            Bambu Studio, PrusaSlicer, Cura, Orca, and all major slicers.
          </DialogDescription>
        </DialogHeader>

        {/* Form */}
        <div className="flex flex-col gap-5 py-2">
          {/* Model name */}
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="print-model-name"
              className="text-xs font-medium text-adam-neutral-300"
            >
              Model Name
            </Label>
            <Input
              id="print-model-name"
              value={settings.modelName}
              onChange={(e) => set('modelName', e.target.value)}
              className="h-9 border-adam-neutral-700 bg-adam-neutral-800 text-sm text-adam-text-primary placeholder:text-adam-neutral-500 focus-visible:ring-adam-blue"
              placeholder="my-model"
              maxLength={64}
            />
          </div>

          {/* Scale + Unit row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label
                htmlFor="print-scale"
                className="text-xs font-medium text-adam-neutral-300"
              >
                Scale
              </Label>
              <div className="relative">
                <Input
                  id="print-scale"
                  type="number"
                  min={0.001}
                  max={100}
                  step={0.1}
                  value={settings.scale}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v) && v > 0) set('scale', v);
                  }}
                  className="h-9 border-adam-neutral-700 bg-adam-neutral-800 pr-8 text-sm text-adam-text-primary focus-visible:ring-adam-blue"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-adam-neutral-500">
                  ×
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label
                htmlFor="print-unit"
                className="text-xs font-medium text-adam-neutral-300"
              >
                Unit
              </Label>
              <Select
                value={settings.unit}
                onValueChange={(v) =>
                  set('unit', v as PrintSettings['unit'])
                }
              >
                <SelectTrigger
                  id="print-unit"
                  className="h-9 border-adam-neutral-700 bg-adam-neutral-800 text-sm text-adam-text-primary focus:ring-adam-blue"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-adam-neutral-700 bg-adam-neutral-800 text-adam-text-primary">
                  <SelectItem value="millimeter">Millimetre (mm)</SelectItem>
                  <SelectItem value="inch">Inch (in)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quality */}
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="print-quality"
              className="text-xs font-medium text-adam-neutral-300"
            >
              Quality Hint
              <span className="ml-1.5 text-[10px] text-adam-neutral-500">
                (stored as metadata — your slicer applies the actual profile)
              </span>
            </Label>
            <Select
              value={settings.quality}
              onValueChange={(v) =>
                set('quality', v as PrintSettings['quality'])
              }
            >
              <SelectTrigger
                id="print-quality"
                className="h-9 border-adam-neutral-700 bg-adam-neutral-800 text-sm text-adam-text-primary focus:ring-adam-blue"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-adam-neutral-700 bg-adam-neutral-800 text-adam-text-primary">
                <SelectItem value="draft">Draft — 0.3 mm layers (fast)</SelectItem>
                <SelectItem value="standard">Standard — 0.2 mm layers</SelectItem>
                <SelectItem value="fine">Fine — 0.1 mm layers (slow)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Toggle row */}
          <div className="flex flex-col gap-3 rounded-lg border border-adam-neutral-700 bg-adam-neutral-800/50 px-4 py-3">
            {/* Centre on plate */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-adam-text-primary">
                  Centre on build plate
                </span>
                <span className="text-[11px] text-adam-neutral-500">
                  Move model to plate origin automatically
                </span>
              </div>
              <Switch
                id="print-centre"
                checked={settings.centreOnPlate}
                onCheckedChange={(v) => set('centreOnPlate', v)}
                className="data-[state=checked]:bg-adam-blue"
              />
            </div>

            {/* Support */}
            <div className="flex items-center justify-between gap-4 border-t border-adam-neutral-700 pt-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-adam-text-primary">
                  Support structures hint
                </span>
                <span className="text-[11px] text-adam-neutral-500">
                  Tag file so slicer can suggest auto-supports
                </span>
              </div>
              <Switch
                id="print-support"
                checked={settings.needsSupport}
                onCheckedChange={(v) => set('needsSupport', v)}
                className="data-[state=checked]:bg-adam-blue"
              />
            </div>
          </div>

          {/* Info banner */}
          <div className="flex items-start gap-2 rounded-lg border border-adam-blue/20 bg-adam-blue/5 px-3 py-2.5">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-adam-blue/70" />
            <p className="text-[11px] leading-relaxed text-adam-neutral-400">
              .3MF is the modern 3D print format. It opens directly in Bambu
              Studio, PrusaSlicer, Cura, and Orca without conversion. Units and
              scale are embedded — no re-scaling needed in the slicer.
            </p>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
            className="text-adam-neutral-400 hover:bg-adam-neutral-800 hover:text-adam-text-primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isExporting || !settings.modelName.trim()}
            className="gap-2 bg-adam-blue hover:bg-adam-blue/90"
            id="print-settings-confirm-btn"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isExporting ? 'Generating…' : 'Download .3MF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
